import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { Box, Button, Step, Stepper, useMediaQuery } from "@mui/material";
import PropTypes from "prop-types";

import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import CommentInputForReason from "Components/Common/CommentInputForReason";
import Modal from "Components/Common/Modal";
import CommentBox from "Components/Common/CommentBox";
import StepLabelField from "common/ApplicationApprovalTimelineChart/StepLabelField";

import {
  useGetApprovalTrackingByTechnicalSanctionIdQuery,
  usePutTechnicalSanctionDocumentApproveMutation,
} from "apis/TechnicalSanction";

const TechnicalSanctionApprovalActionButton = ({
  technicalSanctionData: {
    nextApproveBy,
    nextRevokeBy,
    applicationId,
    technicalSanctionDataId,
  },
  isTechnicalSanctionApproved,
  handleClose,
  commentsData,
  t,
}) => {
  const { user: { id: userId } = {} } = useSelector((state) => state.Login);

  const isMobileDevice = useMediaQuery("(max-width: 768px)");

  const [isRevokedCommentModalVisible, setIsRevokedCommentModalVisible] =
    useState(false);
  const [isApproveButtonClicked, setIsApproveButtonClicked] = useState(false);
  const [isCommentBtnClicked, setIsCommentBtnClicked] = useState(false);
  const [isApprovalTrackingModalOpen, setIsApprovalTrackingModalOpen] =
    useState(false);

  const { data: { data: technicalSanctionApprovalTrackingData = [] } = {} } =
    useGetApprovalTrackingByTechnicalSanctionIdQuery({
      technicalSanctionId: technicalSanctionDataId,
    });

  const [
    approveTechnicalSanction,
    { isLoading: isTechnicalSanctionApproving },
  ] = usePutTechnicalSanctionDocumentApproveMutation();

  const canApprove = useMemo(
    () => nextApproveBy.includes(userId),
    [nextApproveBy, userId]
  );

  const canRevoke = useMemo(
    () => nextRevokeBy.includes(userId),
    [nextRevokeBy, userId]
  );

  const submitHandlerForApproval = async () => {
    try {
      await approveTechnicalSanction({
        applicationIds: [applicationId],
        isApproved: true,
      });

      setIsApproveButtonClicked(false);
      handleClose();
    } catch (err) {}
  };

  const submitHandlerForRevoked = async (comment) => {
    try {
      await approveTechnicalSanction({
        applicationIds: [applicationId],
        isApproved: false,
        comment,
      });

      setIsRevokedCommentModalVisible(false);
      handleClose();
    } catch (err) {}
  };

  return (
    <>
      {isApprovalTrackingModalOpen && (
        <Modal
          isOpen
          title="Approval Chart"
          maxWidth="lg"
          handleClose={() => setIsApprovalTrackingModalOpen(false)}
        >
          <Box sx={{ width: "100%" }}>
            <Stepper
              activeStep={technicalSanctionApprovalTrackingData.length}
              alternativeLabel={!isMobileDevice}
              className="mt-3"
              orientation={isMobileDevice ? "vertical" : "horizontal"}
            >
              {technicalSanctionApprovalTrackingData.map((step, index) => (
                <Step key={index}>
                  <StepLabelField step={step} />
                </Step>
              ))}
            </Stepper>
          </Box>
        </Modal>
      )}

      {isApproveButtonClicked && (
        <ConfirmationDialog
          message="Are you sure you want to approve technical sanction?"
          onCancel={() => {
            setIsApproveButtonClicked(false);
          }}
          onConfirm={submitHandlerForApproval}
          isOpen={isApproveButtonClicked}
          title="Warning"
          variant="warning"
          isLoading={isTechnicalSanctionApproving}
        />
      )}

      <CommentInputForReason
        handleClose={() => {
          setIsRevokedCommentModalVisible(false);
        }}
        handleSubmit={submitHandlerForRevoked}
        isOpen={isRevokedCommentModalVisible}
        isLoading={isTechnicalSanctionApproving && isRevokedCommentModalVisible}
        title="Add Reason for Revoked"
        errorMessage="Please enter reason for revoked"
      />

      {isCommentBtnClicked && (
        <Modal
          isOpen
          title="Comments"
          handleClose={() => setIsCommentBtnClicked(false)}
        >
          <div className="mt-1">
            {commentsData.map(({ comment, createdAt, name }, index) => (
              <CommentBox
                comment={comment}
                name={name}
                createdAt={createdAt}
                key={index}
              />
            ))}
          </div>
        </Modal>
      )}
      {(canApprove ||
        canRevoke ||
        !!commentsData.length ||
        !!technicalSanctionApprovalTrackingData.length) && (
        <div className="text-end">
          {!!technicalSanctionApprovalTrackingData.length && (
            <Button
              variant="contained"
              className="mt-2 me-2"
              color="primary"
              onClick={() => setIsApprovalTrackingModalOpen(true)}
            >
              {t("Tracking")}
            </Button>
          )}
          {!!commentsData.length && !isTechnicalSanctionApproved && (
            <Button
              variant="contained"
              className="mt-2 me-2"
              color="info"
              onClick={() => setIsCommentBtnClicked(true)}
            >
              {t("Comments")}
            </Button>
          )}
          {canRevoke && !isTechnicalSanctionApproved && (
            <Button
              variant="contained"
              className="mt-2 me-2"
              color="warning"
              onClick={() => setIsRevokedCommentModalVisible(true)}
            >
              {t("Revoke")}
            </Button>
          )}
          {canApprove && !isTechnicalSanctionApproved && (
            <Button
              variant="contained"
              className="mt-2 "
              color="success"
              onClick={() => {
                setIsApproveButtonClicked(true);
              }}
            >
              {t("Approve")}
            </Button>
          )}
        </div>
      )}
    </>
  );
};

TechnicalSanctionApprovalActionButton.propTypes = {
  technicalSanctionData: PropTypes.object,
  handleClose: PropTypes.func,
  commentsData: PropTypes.array,
  isTechnicalSanctionApproved: PropTypes.number,
  t: PropTypes.func,
};
export default withTranslation()(TechnicalSanctionApprovalActionButton);
